// Estimator config (the OpenAI/Vertex/Claude chain and the order it runs in)
// for the AI Usage page. Loads GET /api/admin/ai-config and saves partial
// updates via PUT — the same hook shape as useGift so the request/response
// handling stays in one place.
// A 503 `not_configured` means the server has no settings store wired.
import { useEffect, useState } from 'preact/hooks';
import { api, ApiException } from '../../lib/api';

// One link of the chain. Its key is pasted here and stored encrypted in
// app_settings; OPENAI_API_KEY on the server (envKey) overrides it, and then
// model/effort/fast come from the environment too.
export type OpenAiConfig = {
  enabled: boolean;
  model: string;
  effort: string;
  // fast: send service_tier=priority — lower latency at ~2x the token price.
  fast: boolean;
  efforts: string[];
  hasKey: boolean;
  keyHint: string | null;
  keyUnreadable: boolean;
  envKey: boolean;
};

// A provider of the estimate chain, as the server names it.
export type AiProvider = { id: string; name: string };

export type AiConfig = {
  openai: OpenAiConfig;
  // providerOrder: the chain in the order it actually runs (provider ids).
  // providers: the known set with display names — the order picker's labels.
  providerOrder: string[];
  providers: AiProvider[];
  fallbackEnabled: boolean;
  model: string;
  // effort: output_config.effort for BOTH Claude links (Anthropic key and
  // Vertex Claude); editable even when the key/model come from the env.
  effort: string;
  efforts: string[];
  hasKey: boolean;
  keyHint: string | null;
  // envKey: the server has ANTHROPIC_API_KEY set, which overrides any stored
  // key. keyEditable: SETTINGS_ENC_KEY is present, so a key can be stored here.
  envKey: boolean;
  keyEditable: boolean;
  // keyDerived: the encryption key comes from JWT_SECRET, so rotating that
  // secret means re-pasting every stored key.
  keyDerived: boolean;
};

export type AiConfigPatch = {
  apiKey?: string; // non-empty = store; '' = clear; absent = unchanged
  model?: string;
  fallbackEnabled?: boolean;
  effort?: string;
  openaiApiKey?: string; // same semantics as apiKey
  openaiModel?: string;
  openaiEffort?: string;
  openaiEnabled?: boolean;
  openaiFast?: boolean;
  // providerOrder: every provider id exactly once; [] restores the default.
  providerOrder?: string[];
};

export type AiConfigMsg = { ok: boolean; text: string } | null;

export function useAiConfig() {
  const [config, setConfig] = useState<AiConfig | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<AiConfigMsg>(null);

  useEffect(() => {
    let cancelled = false;
    api<AiConfig>('/api/admin/ai-config')
      .then((c) => { if (!cancelled) setConfig(c); })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiException && e.code === 'not_configured') setNotConfigured(true);
        else setMsg({ ok: false, text: e instanceof Error ? e.message : 'Failed to load AI config' });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const save = async (patch: AiConfigPatch, okText: string): Promise<boolean> => {
    if (busy) return false;
    setBusy(true);
    setMsg(null);
    try {
      const c = await api<AiConfig>('/api/admin/ai-config', {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
      setConfig(c);
      setMsg({ ok: true, text: okText });
      return true;
    } catch (e) {
      if (e instanceof ApiException && e.code === 'not_configured') {
        setNotConfigured(true);
      } else {
        setMsg({ ok: false, text: e instanceof ApiException ? e.message : 'Request failed' });
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { config, notConfigured, loading, busy, msg, setMsg, save };
}
